// HKDSE 2017 Paper 1 Knowledge Graph Samples
// Organized by question types with systematic naming

export const hkdse2017Samples = {
  algebra: [
    {
      id: 'hkdse2017_algebra_01',
      problem: "Given that $y$ is the subject of the formula $k = \\frac{3x - y}{y}$.",
      problemType: "algebra_formula",
      difficulty: "fundamental",
      description: "Rearranging formula to make a variable the subject",
      graphData: {
        nodes: [
          {
            id: "fraction_equation",
            name: "Fraction Equation",
            type: "concepts",
            description: "Equation with fraction involving variable y",
            value: "k = (3x - y)/y",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "cross_multiplication",
            name: "Cross Multiplication",
            type: "concepts",
            description: "Method to eliminate fraction by multiplying both sides",
            value: "If a/b = c, then a = bc",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "step1_cross_multiply",
            name: "Step 1: Cross Multiply",
            type: "steps",
            description: "Multiply both sides by y",
            value: "ky = 3x - y",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step2_collect_terms",
            name: "Step 2: Collect Terms",
            type: "steps",
            description: "Collect all terms with y on one side",
            value: "ky + y = 3x",
            importance: 8,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_factor_y",
            name: "Step 3: Factor y",
            type: "steps",
            description: "Factor out y from left side",
            value: "y(k + 1) = 3x",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "step4_solve_for_y",
            name: "Step 4: Solve for y",
            type: "steps",
            description: "Divide both sides by (k + 1)",
            value: "y = 3x/(k + 1)",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "y expressed in terms of x and k",
            value: "y = 3x/(k + 1)",
            importance: 10,
            x: 0, y: 300, z: 0
          }
        ],
        links: [
          { from: "fraction_equation", to: "cross_multiplication", type: "requires", strength: 9 },
          { from: "cross_multiplication", to: "step1_cross_multiply", type: "enables", strength: 10 },
          { from: "step1_cross_multiply", to: "step2_collect_terms", type: "leads to", strength: 9 },
          { from: "step2_collect_terms", to: "step3_factor_y", type: "leads to", strength: 10 },
          { from: "step3_factor_y", to: "step4_solve_for_y", type: "leads to", strength: 10 },
          { from: "step4_solve_for_y", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'hkdse2017_algebra_02',
      problem: "Simplify $\\frac{(m^4 n^{-1})^3}{(m^{-2})^5}$ and express the answer in positive index form.",
      problemType: "algebra_indices",
      difficulty: "intermediate",
      description: "Simplifying expressions with negative indices",
      graphData: {
        nodes: [
          {
            id: "negative_indices",
            name: "Negative Indices",
            type: "concepts",
            description: "Understanding negative indices and their properties",
            value: "a⁻ⁿ = 1/aⁿ",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "power_of_power",
            name: "Power of Power",
            type: "concepts",
            description: "When raising a power to another power, multiply the indices",
            value: "(aᵐ)ⁿ = aᵐⁿ",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "division_of_powers",
            name: "Division of Powers",
            type: "concepts",
            description: "When dividing powers with the same base, subtract the indices",
            value: "aᵐ ÷ aⁿ = aᵐ⁻ⁿ",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_simplify_numerator",
            name: "Step 1: Simplify Numerator",
            type: "steps",
            description: "Apply power of power rule to (m⁴n⁻¹)³",
            value: "(m⁴n⁻¹)³ = m¹²n⁻³",
            importance: 8,
            x: 0, y: 0, z: 25
          },
          {
            id: "step2_simplify_denominator",
            name: "Step 2: Simplify Denominator",
            type: "steps",
            description: "Apply power of power rule to (m⁻²)⁵",
            value: "(m⁻²)⁵ = m⁻¹⁰",
            importance: 8,
            x: 0, y: 100, z: -15
          },
          {
            id: "step3_divide",
            name: "Step 3: Divide",
            type: "steps",
            description: "Divide m¹²n⁻³ by m⁻¹⁰",
            value: "m¹²n⁻³ ÷ m⁻¹⁰ = m²²n⁻³",
            importance: 9,
            x: 0, y: 200, z: 0
          },
          {
            id: "step4_convert_negative",
            name: "Step 4: Convert to Positive Indices",
            type: "steps",
            description: "Convert negative indices to positive using reciprocal",
            value: "m²²n⁻³ = m²²/n³",
            importance: 10,
            x: 0, y: 300, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "The simplified expression in positive indices",
            value: "m²²/n³",
            importance: 10,
            x: 0, y: 400, z: 0
          }
        ],
        links: [
          { from: "negative_indices", to: "step4_convert_negative", type: "enables", strength: 10 },
          { from: "power_of_power", to: "step1_simplify_numerator", type: "enables", strength: 9 },
          { from: "power_of_power", to: "step2_simplify_denominator", type: "enables", strength: 9 },
          { from: "division_of_powers", to: "step3_divide", type: "enables", strength: 9 },
          { from: "step1_simplify_numerator", to: "step3_divide", type: "leads to", strength: 10 },
          { from: "step2_simplify_denominator", to: "step3_divide", type: "leads to", strength: 10 },
          { from: "step3_divide", to: "step4_convert_negative", type: "leads to", strength: 10 },
          { from: "step4_convert_negative", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  factorization: [
    {
      id: 'hkdse2017_factorization_01',
      problem: "Factorize: (a) $x^2 - 4xy + 3y^2$ (b) $x^2 - 4xy + 3y^2 + 11x - 33y$",
      problemType: "factorization",
      difficulty: "intermediate",
      description: "Factorization of polynomial expressions",
      graphData: {
        nodes: [
          {
            id: "quadratic_factorization",
            name: "Quadratic Factorization",
            type: "concepts",
            description: "Factoring quadratic expressions",
            value: "ax² + bx + c = (px + q)(rx + s)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "grouping_method",
            name: "Grouping Method",
            type: "concepts",
            description: "Grouping terms to find common factors",
            value: "Group terms with common factors",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "step1_part_a",
            name: "Step 1: Part (a) - Factor Quadratic",
            type: "steps",
            description: "Factor x² - 4xy + 3y²",
            value: "(x - y)(x - 3y)",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "step2_part_b",
            name: "Step 2: Part (b) - Group Terms",
            type: "steps",
            description: "Group terms: (x² - 4xy + 3y²) + (11x - 33y)",
            value: "(x - y)(x - 3y) + 11(x - 3y)",
            importance: 8,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_part_b",
            name: "Step 3: Part (b) - Factor Further",
            type: "steps",
            description: "Factor out common terms",
            value: "(x - 3y)(x - y + 11)",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "Fully factorized expressions",
            value: "(a) (x - y)(x - 3y), (b) (x - 3y)(x - y + 11)",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "quadratic_factorization", to: "step1_part_a", type: "enables", strength: 10 },
          { from: "grouping_method", to: "step2_part_b", type: "enables", strength: 9 },
          { from: "step1_part_a", to: "step2_part_b", type: "leads to", strength: 8 },
          { from: "step2_part_b", to: "step3_part_b", type: "leads to", strength: 10 },
          { from: "step3_part_b", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    }
  ]
}; 